import useCancellationOffer from 'features/promotions/hooks/useCancellationOffer';
import { useCallback } from 'react';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppSelector } from 'store/hooks';
import { selectAbFairPrice, selectAbSkipNextPayment } from '../selectors';
import useNavigateToCancelConfirm from './useNavigateToCancelConfirm';
const useNavigateToDiscountStep = (tier, loadOnMount) => {
    const navigation = useMainStackNavigation();
    const { cancellationDiscountDataRef, fairPriceOffersRef, skipNextPaymentOfferRef, isFetching, retry, } = useCancellationOffer(loadOnMount);
    const abSkipNextPayment = useAppSelector(selectAbSkipNextPayment);
    const abFairPrice = useAppSelector(selectAbFairPrice);
    const navigateToCancelConfirm = useNavigateToCancelConfirm();
    const fetchDiscounts = useCallback(async () => {
        if (cancellationDiscountDataRef.current?.length) {
            return cancellationDiscountDataRef.current;
        }
        await retry();
        return cancellationDiscountDataRef.current?.length
            ? cancellationDiscountDataRef.current
            : null;
    }, [cancellationDiscountDataRef, retry]);
    const navigateToDiscountStep = useCallback(async (callback, discountData, skipFetch) => {
        if (abSkipNextPayment && skipNextPaymentOfferRef.current) {
            navigation.navigate('ManagePremiumSubscriptionSkipNextPayment', {
                offer: skipNextPaymentOfferRef.current.offer,
                currency: skipNextPaymentOfferRef.current.currency,
                tier,
                callback,
            });
            return;
        }
        if (abFairPrice && fairPriceOffersRef.current) {
            navigation.navigate('ManagePremiumSubscriptionFairPrice', {
                offers: fairPriceOffersRef.current.fairPriceOffers,
                currency: fairPriceOffersRef.current.currency,
                tier,
                source: 'unsubscribe',
                callback,
            });
            return;
        }
        // If discountData is provided, use it directly
        if (discountData) {
            navigation.navigateDeprecated('ManagePremiumSubscriptionCancelDiscount', {
                discountData,
                tier,
                callback,
            });
            return;
        }
        // If skipFetch is true, go directly to fallback
        if (skipFetch) {
            if (callback) {
                callback();
            }
            else {
                navigateToCancelConfirm();
            }
            return;
        }
        // Default behavior: fetch discounts and navigate accordingly
        const discounts = await fetchDiscounts();
        if (discounts) {
            navigation.navigateDeprecated('ManagePremiumSubscriptionCancelDiscount', {
                discountData: discounts,
                tier,
                callback,
            });
        }
        else if (callback) {
            callback();
        }
        else {
            navigateToCancelConfirm();
        }
    }, [
        abFairPrice,
        fairPriceOffersRef,
        abSkipNextPayment,
        skipNextPaymentOfferRef,
        fetchDiscounts,
        tier,
        navigateToCancelConfirm,
    ]);
    return { navigateToDiscountStep, isFetching, fetchDiscounts };
};
export default useNavigateToDiscountStep;
